/**
 * Delivery Date & Time Picker
 * Handles date selection with cut-off time logic for same-day and next-day delivery
 * Supports multi-date selection for subscriptions
 * Timezone: Hong Kong (UTC+8)
 */

class DeliveryDatePicker extends HTMLElement {
  constructor() {
    super();
    this.currentMonth = new Date();
    this.selectedDates = []; // Array for multi-date selection
    this.selectedTimeSlot = null;
    this.HK_TIMEZONE = 'Asia/Hong_Kong';
  }

  connectedCallback() {
    this.initializeSettings();
    this.cacheElements();
    this.bindEvents();
    this.renderCalendar();
    this.renderSelectedDates();
    this.interceptFormSubmit();
  }

  initializeSettings() {
    // Lead time settings (single dropdown: same_day, next_day, 2_days, 3_days, etc.)
    this.minLeadTime = this.dataset.minLeadTime || 'same_day';
    this.leadTimeCutoff = this.dataset.leadTimeCutoff || '12:00';

    // Parse minLeadTime to get number of days
    this.minDaysAdvance = this.parseLeadTimeToDays(this.minLeadTime);

    this.daysAvailable = parseInt(this.dataset.daysAvailable) || 60;

    // Number of dates the customer must select (for subscriptions)
    this.numDatesToSelect = parseInt(this.dataset.numDatesToSelect) || 1;

    // Specific dates mode - if specific dates are provided, ONLY these dates are available
    this.specificDates = [];
    if (this.dataset.specificDates) {
      const today = this.getHKTime();
      const todayStr = this.formatDateISO(today);

      this.specificDates = this.dataset.specificDates
        .split(',')
        .map(d => d.trim())
        .filter(d => d && d.match(/^\d{4}-\d{2}-\d{2}$/) && d >= todayStr)
        .sort();
    }
    this.useSpecificDates = this.specificDates.length > 0;

    // Blocked days of week (0 = Sunday, 6 = Saturday)
    this.blockedDaysOfWeek = [];
    if (this.dataset.blockSunday === 'true') this.blockedDaysOfWeek.push(0);
    if (this.dataset.blockMonday === 'true') this.blockedDaysOfWeek.push(1);
    if (this.dataset.blockTuesday === 'true') this.blockedDaysOfWeek.push(2);
    if (this.dataset.blockWednesday === 'true') this.blockedDaysOfWeek.push(3);
    if (this.dataset.blockThursday === 'true') this.blockedDaysOfWeek.push(4);
    if (this.dataset.blockFriday === 'true') this.blockedDaysOfWeek.push(5);
    if (this.dataset.blockSaturday === 'true') this.blockedDaysOfWeek.push(6);

    // Blocked specific dates
    this.blockedDates = [];
    if (this.dataset.blockedDates) {
      this.blockedDates = this.dataset.blockedDates
        .split(',')
        .map(d => d.trim())
        .filter(d => d && d.match(/^\d{4}-\d{2}-\d{2}$/));
    }

    // Time slots
    this.timeSlots = [];
    if (this.dataset.timeSlots) {
      const slotsRaw = this.dataset.timeSlots.split('|||');
      slotsRaw.forEach(slot => {
        const trimmed = slot.trim();
        if (trimmed) {
          const parts = trimmed.split('|');
          if (parts.length >= 2) {
            this.timeSlots.push({
              range: parts[0].trim(),
              label: parts[1].trim()
            });
          } else {
            // If no label, use the range as label
            this.timeSlots.push({
              range: trimmed,
              label: trimmed
            });
          }
        }
      });
    }

    // Error messages
    this.errorDateRequired = this.dataset.errorDate || 'Please select a delivery date';
    this.errorTimeRequired = this.dataset.errorTime || 'Please select a delivery time';
  }

  cacheElements() {
    this.dateInput = this.querySelector('.delivery-picker__date-input');
    this.calendar = this.querySelector('.delivery-picker__calendar');
    this.monthYearLabel = this.querySelector('.delivery-picker__month-year');
    this.daysContainer = this.querySelector('.delivery-picker__days');
    this.prevButton = this.querySelector('.delivery-picker__nav--prev');
    this.nextButton = this.querySelector('.delivery-picker__nav--next');
    this.timeSlotsContainer = this.querySelector('.delivery-picker__time-slots');
    this.selectedDatesContainer = this.querySelector('.delivery-picker__selected-dates');

    // Hidden form inputs
    this.deliveryDateProperty = this.querySelector('[name="properties[Delivery Date]"]');
    this.deliveryTimeProperty = this.querySelector('[name="properties[Delivery Time]"]');
    this.deliveryPropertyInput = this.querySelector('[name="properties[Delivery]"]');

    // Error elements
    this.dateError = this.querySelector('.delivery-picker__field--date .delivery-picker__error');
    this.timeError = this.querySelector('.delivery-picker__field--time .delivery-picker__error');
  }

  bindEvents() {
    // Toggle calendar on input click
    this.dateInput.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleCalendar();
    });

    // Navigation buttons
    this.prevButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.navigateMonth(-1);
    });

    this.nextButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.navigateMonth(1);
    });

    // Close calendar when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.closeCalendar();
      }
    });

    // Day selection
    this.daysContainer.addEventListener('click', (e) => {
      const dayButton = e.target.closest('.delivery-picker__day:not(.disabled):not(.blocked)');
      if (dayButton) {
        this.toggleDate(dayButton);
      }
    });
  }

  /**
   * Parse lead time setting to number of days
   * same_day = 0, next_day = 1, 2_days = 2, etc.
   */
  parseLeadTimeToDays(leadTime) {
    switch (leadTime) {
      case 'same_day': return 0;
      case 'next_day': return 1;
      case '2_days': return 2;
      case '3_days': return 3;
      case '4_days': return 4;
      case '5_days': return 5;
      case '6_days': return 6;
      case '7_days': return 7;
      default: return 0;
    }
  }

  /**
   * Get current time in Hong Kong timezone
   */
  getHKTime() {
    return new Date(new Date().toLocaleString('en-US', { timeZone: this.HK_TIMEZONE }));
  }

  /**
   * Format date to YYYY-MM-DD
   */
  formatDateISO(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Format date for display: Mon, Dec 25, 2024
   */
  formatDateDisplay(date) {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }

  /**
   * Format date short: Dec 25
   */
  formatDateShort(date) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}`;
  }

  /**
   * Check if we're before the cut-off time
   */
  isBeforeCutoff() {
    const now = this.getHKTime();
    const [hours, minutes] = this.leadTimeCutoff.split(':').map(Number);
    const cutoffTime = new Date(now);
    cutoffTime.setHours(hours, minutes, 0, 0);

    return now < cutoffTime;
  }

  /**
   * Get the minimum selectable date based on lead time settings and cut-off time
   */
  getMinDate() {
    const now = this.getHKTime();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Calculate base days to add
    let daysToAdd = this.minDaysAdvance;

    // If we're past the cut-off time, add one more day
    if (!this.isBeforeCutoff()) {
      daysToAdd += 1;
    }

    const minDate = new Date(today);
    minDate.setDate(minDate.getDate() + daysToAdd);
    return minDate;
  }

  /**
   * Get the maximum selectable date
   */
  getMaxDate() {
    const today = this.getHKTime();
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + this.daysAvailable);
    return maxDate;
  }

  /**
   * Check if a date is blocked
   */
  isDateBlocked(date) {
    // Check if day of week is blocked
    if (this.blockedDaysOfWeek.includes(date.getDay())) {
      return true;
    }

    // Check if specific date is blocked
    const dateStr = this.formatDateISO(date);
    if (this.blockedDates.includes(dateStr)) {
      return true;
    }

    return false;
  }

  /**
   * Check if a date is selectable
   */
  isDateSelectable(date) {
    const minDate = this.getMinDate();
    const maxDate = this.getMaxDate();
    const dateStr = this.formatDateISO(date);

    // Reset time parts for comparison
    const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const compareMin = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
    const compareMax = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());

    if (compareDate < compareMin || compareDate > compareMax) {
      return false;
    }

    // If using specific dates mode, check if date is in the list
    if (this.useSpecificDates && !this.specificDates.includes(dateStr)) {
      return false;
    }

    if (this.isDateBlocked(date)) {
      return false;
    }

    return true;
  }

  /**
   * Check if a date is already selected
   */
  isDateSelected(dateStr) {
    return this.selectedDates.includes(dateStr);
  }

  /**
   * Toggle calendar visibility
   */
  toggleCalendar() {
    this.calendar.classList.toggle('active');
  }

  /**
   * Close calendar
   */
  closeCalendar() {
    this.calendar.classList.remove('active');
  }

  /**
   * Navigate to previous/next month
   */
  navigateMonth(direction) {
    this.currentMonth.setMonth(this.currentMonth.getMonth() + direction);
    this.renderCalendar();
  }

  /**
   * Render the calendar
   */
  renderCalendar() {
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();

    // Update month/year label
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'];
    this.monthYearLabel.textContent = `${months[month]} ${year}`;

    // Get first day of month and total days
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Clear previous days
    this.daysContainer.innerHTML = '';

    // Add empty cells for days before first day of month
    for (let i = 0; i < firstDay; i++) {
      const emptyDay = document.createElement('span');
      emptyDay.className = 'delivery-picker__day empty';
      this.daysContainer.appendChild(emptyDay);
    }

    // Add days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = this.formatDateISO(date);
      const dayElement = document.createElement('button');
      dayElement.type = 'button';
      dayElement.className = 'delivery-picker__day';
      dayElement.textContent = day;
      dayElement.dataset.date = dateStr;

      if (!this.isDateSelectable(date)) {
        if (this.isDateBlocked(date)) {
          dayElement.classList.add('blocked');
        } else {
          dayElement.classList.add('disabled');
        }
        dayElement.disabled = true;
      }

      // Mark selected dates
      if (this.isDateSelected(dateStr)) {
        dayElement.classList.add('selected');
      }

      // Mark today
      const today = this.getHKTime();
      if (dateStr === this.formatDateISO(today)) {
        dayElement.classList.add('today');
      }

      this.daysContainer.appendChild(dayElement);
    }

    // Update navigation button states
    this.updateNavButtons();
  }

  /**
   * Update navigation button states
   */
  updateNavButtons() {
    const minDate = this.getMinDate();
    const maxDate = this.getMaxDate();

    // Disable prev if current month is before or same as min date month
    const minMonth = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    const currentMonthStart = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), 1);
    this.prevButton.disabled = currentMonthStart <= minMonth;

    // Disable next if current month is same as max date month
    const maxMonth = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
    this.nextButton.disabled = currentMonthStart >= maxMonth;
  }

  /**
   * Toggle date selection (for multi-date mode)
   */
  toggleDate(dayElement) {
    const dateStr = dayElement.dataset.date;

    if (this.isDateSelected(dateStr)) {
      // Deselect the date
      this.selectedDates = this.selectedDates.filter(d => d !== dateStr);
      dayElement.classList.remove('selected');
    } else {
      // Check if we've reached the limit
      if (this.selectedDates.length >= this.numDatesToSelect) {
        if (this.numDatesToSelect === 1) {
          // Single date mode - replace the existing selection
          this.selectedDates = [dateStr];
          // Update UI
          this.daysContainer.querySelectorAll('.selected').forEach(el => el.classList.remove('selected'));
          dayElement.classList.add('selected');
        } else {
          // Multi-date mode - show message
          this.showError('date', `You can only select ${this.numDatesToSelect} dates`);
          return;
        }
      } else {
        // Add the date
        this.selectedDates.push(dateStr);
        this.selectedDates.sort(); // Keep dates in order
        dayElement.classList.add('selected');
      }
    }

    // Update the selected dates display
    this.renderSelectedDates();

    // Update the input display
    this.updateDateInput();

    // Clear date error
    this.clearError('date');

    // If single date mode, close calendar after selection
    if (this.numDatesToSelect === 1 && this.selectedDates.length === 1) {
      this.closeCalendar();
    }

    // Render time slots if we have at least one date selected
    if (this.selectedDates.length > 0) {
      this.renderTimeSlots();
    } else {
      this.timeSlotsContainer.innerHTML = `<p class="delivery-picker__time-placeholder">Please select ${this.numDatesToSelect > 1 ? 'dates' : 'a date'} first</p>`;
      this.selectedTimeSlot = null;
      this.updateHiddenInputs();
    }
  }

  /**
   * Render selected dates as tags
   */
  renderSelectedDates() {
    if (!this.selectedDatesContainer) return;

    if (this.selectedDates.length === 0) {
      this.selectedDatesContainer.innerHTML = '';
      return;
    }

    let html = '<div class="delivery-picker__date-tags">';
    this.selectedDates.forEach((dateStr, index) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      html += `
        <span class="delivery-picker__date-tag">
          <span class="delivery-picker__date-tag-number">${index + 1}.</span>
          <span class="delivery-picker__date-tag-text">${this.formatDateShort(date)}</span>
          <button type="button" class="delivery-picker__date-tag-remove" data-date="${dateStr}" aria-label="Remove date">×</button>
        </span>
      `;
    });
    html += '</div>';

    if (this.numDatesToSelect > 1) {
      html += `<p class="delivery-picker__date-count">${this.selectedDates.length} of ${this.numDatesToSelect} dates selected</p>`;
    }

    this.selectedDatesContainer.innerHTML = html;

    // Bind remove events
    this.selectedDatesContainer.querySelectorAll('.delivery-picker__date-tag-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const dateStr = btn.dataset.date;
        this.selectedDates = this.selectedDates.filter(d => d !== dateStr);
        this.renderSelectedDates();
        this.updateDateInput();
        this.renderCalendar();
        if (this.selectedDates.length > 0) {
          this.renderTimeSlots();
        } else {
          this.timeSlotsContainer.innerHTML = `<p class="delivery-picker__time-placeholder">Please select ${this.numDatesToSelect > 1 ? 'dates' : 'a date'} first</p>`;
          this.selectedTimeSlot = null;
          this.updateHiddenInputs();
        }
      });
    });
  }

  /**
   * Update the date input display
   */
  updateDateInput() {
    if (this.selectedDates.length === 0) {
      this.dateInput.value = '';
    } else if (this.selectedDates.length === 1) {
      const [year, month, day] = this.selectedDates[0].split('-').map(Number);
      const date = new Date(year, month - 1, day);
      this.dateInput.value = this.formatDateDisplay(date);
    } else {
      this.dateInput.value = `${this.selectedDates.length} dates selected`;
    }
  }

  /**
   * Check if any selected date is today (same-day delivery)
   */
  hasSelectedDateToday() {
    const today = this.getHKTime();
    const todayStr = this.formatDateISO(today);
    return this.selectedDates.includes(todayStr);
  }

  /**
   * Check if a time slot is a morning slot
   * Morning slots are typically before 12pm/noon
   */
  isMorningSlot(slot) {
    // Check the label first (case-insensitive)
    const label = slot.label.toLowerCase();
    if (label.includes('morning') || label.includes('am')) {
      return true;
    }

    // Check the range - parse the start time
    const range = slot.range.toLowerCase();
    // Match patterns like "9am", "10:00am", "9:00 am", "09:00"
    const timeMatch = range.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);

    if (timeMatch) {
      let hour = parseInt(timeMatch[1]);
      const period = timeMatch[3] ? timeMatch[3].toLowerCase() : null;

      // Convert to 24-hour format
      if (period === 'pm' && hour !== 12) {
        hour += 12;
      } else if (period === 'am' && hour === 12) {
        hour = 0;
      }

      // If no am/pm specified, assume times 1-11 are AM if less than 12
      // Morning is before 12:00 (noon)
      return hour < 12;
    }

    return false;
  }

  /**
   * Render time slots
   */
  renderTimeSlots() {
    this.timeSlotsContainer.innerHTML = '';
    this.selectedTimeSlot = null;
    this.updateHiddenInputs();

    if (this.selectedDates.length === 0) {
      this.timeSlotsContainer.innerHTML = `<p class="delivery-picker__time-placeholder">Please select ${this.numDatesToSelect > 1 ? 'dates' : 'a date'} first</p>`;
      return;
    }

    if (this.timeSlots.length === 0) {
      this.timeSlotsContainer.innerHTML = '<p class="delivery-picker__time-placeholder">No time slots available</p>';
      return;
    }

    // Check if any selected date is today (same-day delivery)
    const hasSameDay = this.hasSelectedDateToday();

    // Filter time slots - for same-day, only show afternoon slots
    const availableSlots = this.timeSlots.filter(slot => {
      if (hasSameDay) {
        // For same-day delivery, exclude morning slots
        return !this.isMorningSlot(slot);
      }
      return true;
    });

    if (availableSlots.length === 0) {
      this.timeSlotsContainer.innerHTML = '<p class="delivery-picker__time-placeholder">No time slots available for the selected dates</p>';
      return;
    }

    availableSlots.forEach((slot, index) => {
      const slotButton = document.createElement('button');
      slotButton.type = 'button';
      slotButton.className = 'delivery-picker__time-slot';
      slotButton.dataset.range = slot.range;
      slotButton.dataset.label = slot.label;

      slotButton.innerHTML = `
        <span class="delivery-picker__time-label">${slot.label}</span>
        <span class="delivery-picker__time-range">${slot.range}</span>
      `;

      slotButton.addEventListener('click', () => {
        this.selectTimeSlot(slotButton, slot);
      });

      this.timeSlotsContainer.appendChild(slotButton);
    });
  }

  /**
   * Select a time slot
   */
  selectTimeSlot(slotButton, slot) {
    // Remove previous selection
    const previousSelected = this.timeSlotsContainer.querySelector('.selected');
    if (previousSelected) {
      previousSelected.classList.remove('selected');
    }

    // Mark new selection
    slotButton.classList.add('selected');
    this.selectedTimeSlot = slot;

    // Update hidden inputs
    this.updateHiddenInputs();

    // Clear time error
    this.clearError('time');
  }

  /**
   * Update hidden form inputs with selected dates and time
   */
  updateHiddenInputs() {
    if (this.selectedDates.length === 0 || !this.selectedTimeSlot) {
      if (this.deliveryDateProperty) this.deliveryDateProperty.value = '';
      if (this.deliveryTimeProperty) this.deliveryTimeProperty.value = '';
      if (this.deliveryPropertyInput) this.deliveryPropertyInput.value = '';
      return;
    }

    // Format dates for display
    const formattedDates = this.selectedDates.map(dateStr => {
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return this.formatDateDisplay(date);
    });

    // Set individual properties (with null checks)
    if (this.deliveryDateProperty) {
      this.deliveryDateProperty.value = formattedDates.join(' | ');
    }
    if (this.deliveryTimeProperty) {
      this.deliveryTimeProperty.value = `${this.selectedTimeSlot.label} (${this.selectedTimeSlot.range})`;
    }

    // Create full delivery string
    if (this.deliveryPropertyInput) {
      if (formattedDates.length === 1) {
        this.deliveryPropertyInput.value = `${formattedDates[0]} - ${this.selectedTimeSlot.label} (${this.selectedTimeSlot.range})`;
      } else {
        this.deliveryPropertyInput.value = `${formattedDates.length} deliveries: ${formattedDates.join(' | ')} - ${this.selectedTimeSlot.label} (${this.selectedTimeSlot.range})`;
      }
    }
  }

  /**
   * Show error message
   */
  showError(type, message) {
    if (type === 'date') {
      this.dateError.textContent = message;
      this.dateError.classList.add('active');
      this.dateInput.classList.add('error');
    } else if (type === 'time') {
      this.timeError.textContent = message;
      this.timeError.classList.add('active');
      this.timeSlotsContainer.classList.add('error');
    }
  }

  /**
   * Clear error message
   */
  clearError(type) {
    if (type === 'date') {
      this.dateError.textContent = '';
      this.dateError.classList.remove('active');
      this.dateInput.classList.remove('error');
    } else if (type === 'time') {
      this.timeError.textContent = '';
      this.timeError.classList.remove('active');
      this.timeSlotsContainer.classList.remove('error');
    }
  }

  /**
   * Validate selection
   */
  validate() {
    let isValid = true;

    if (this.selectedDates.length === 0) {
      this.showError('date', this.errorDateRequired);
      isValid = false;
    } else if (this.selectedDates.length < this.numDatesToSelect) {
      this.showError('date', `Please select ${this.numDatesToSelect} date${this.numDatesToSelect > 1 ? 's' : ''}`);
      isValid = false;
    }

    if (!this.selectedTimeSlot) {
      this.showError('time', this.errorTimeRequired);
      isValid = false;
    }

    return isValid;
  }

  /**
   * Intercept form submission to validate
   */
  interceptFormSubmit() {
    const self = this;

    // Find the product form by ID
    const formId = this.deliveryPropertyInput ? this.deliveryPropertyInput.getAttribute('form') : null;
    const productForm = formId ? document.getElementById(formId) : null;

    // Also find the product-form custom element
    const productFormElement = this.closest('product-form') || document.querySelector('product-form');

    // Handle traditional form submission with capturing phase to run before other handlers
    if (productForm) {
      productForm.addEventListener('submit', (e) => {
        if (!self.validate()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          self.scrollToError();
          return false;
        }
      }, true); // Use capturing phase
    }

    // Handle product-form custom element's submit button click
    if (productFormElement) {
      const submitButton = productFormElement.querySelector('[type="submit"]');
      if (submitButton) {
        // Add click handler in capturing phase to validate before the form's click handler
        submitButton.addEventListener('click', (e) => {
          if (!self.validate()) {
            e.preventDefault();
            e.stopImmediatePropagation();
            self.scrollToError();
            return false;
          }
        }, true); // Use capturing phase to run first
      }

      // Also intercept the form inside product-form if it exists
      const innerForm = productFormElement.querySelector('form');
      if (innerForm && innerForm !== productForm) {
        innerForm.addEventListener('submit', (e) => {
          if (!self.validate()) {
            e.preventDefault();
            e.stopImmediatePropagation();
            self.scrollToError();
            return false;
          }
        }, true); // Use capturing phase
      }
    }

    // Additionally, disable the submit button until valid selections are made
    this.updateSubmitButtonState();
  }

  /**
   * Scroll to the first error
   */
  scrollToError() {
    const firstError = this.querySelector('.delivery-picker__error.active');
    if (firstError) {
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      // Scroll to the picker itself
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /**
   * Update submit button state based on selection validity
   */
  updateSubmitButtonState() {
    const productFormElement = this.closest('product-form') || document.querySelector('product-form');
    if (!productFormElement) return;

    const submitButton = productFormElement.querySelector('[type="submit"]');
    if (!submitButton) return;

    // Check if both date and time are selected
    const isComplete = this.selectedDates.length >= this.numDatesToSelect && this.selectedTimeSlot !== null;

    // We don't actually disable the button, but we could add a visual indicator
    // The validation will still block the submission
  }
}

// Define the custom element
if (!customElements.get('delivery-date-picker')) {
  customElements.define('delivery-date-picker', DeliveryDatePicker);
}

// Auto-initialize for non-custom-element usage
document.addEventListener('DOMContentLoaded', () => {
  const pickers = document.querySelectorAll('.delivery-picker:not(delivery-date-picker)');
  pickers.forEach(picker => {
    // Upgrade to custom element behavior
    if (!picker.matches('delivery-date-picker')) {
      const wrapper = document.createElement('delivery-date-picker');
      wrapper.innerHTML = picker.innerHTML;

      // Copy data attributes
      Array.from(picker.attributes).forEach(attr => {
        wrapper.setAttribute(attr.name, attr.value);
      });

      picker.parentNode.replaceChild(wrapper, picker);
    }
  });
});
